import { Upload, Flex, message, Spin } from 'antd';
import { RcFile } from 'antd/es/upload/interface';
import { BarIcon, TrashIcon } from '@/components';
import { useTranslation } from 'react-i18next';
import { useUploadExcel } from '@/hooksApi/useImport';
import { mutateFunctionType } from '@/hooks/useFileUpload';
import { Upload as UploadIcon } from 'lucide-react';

const { Dragger } = Upload;

interface StepProps {
  uploadedFile: RcFile | null;
  uploadFile: (mutateFunction: mutateFunctionType, file?: RcFile) => Promise<void>;
  handleBeforeUpload: (file: RcFile) => string | false;
  handleResetUploadedFile: () => void;
}

const Step1 = ({
  handleResetUploadedFile,
  uploadedFile,
  handleBeforeUpload,
  uploadFile,
}: StepProps) => {
  const { t } = useTranslation();
  const { mutate: UploadExcel, isLoading: loadingExcel } = useUploadExcel();

  const handleDeleteFile = () => {
    handleResetUploadedFile();

    if (uploadedFile) {
      message.info(`${uploadedFile?.name} ${t('file_removed')}`);
    }
  };

  const handleUpload = (file: RcFile) => {
    const validationErrorReason = handleBeforeUpload(file);

    if (!validationErrorReason) uploadFile(UploadExcel, file);
  };

  return (
    <div className='space-y-6'>
      {/* Import Instructions Card */}
      <div className='border border-[rgb(232,230,227)] rounded-2xl p-6 bg-white'>
        <h3 className='text-base font-semibold mb-4 normal-case text-sd-foreground' style={{ marginTop: 0 }}>
          {String(t('import_instructions'))}
        </h3>
        <div className='space-y-3 text-sm text-[#737b8c]'>
          <p style={{ margin: 0 }}>
            <span className='font-semibold text-sd-foreground'>
              1. {String(t('download_template'))}:
            </span>{' '}
            {String(t('start_download'))}{' '}
            <a
              href='/boxy-bulk-import.csv'
              target='_blank'
              rel='noreferrer'
              className='text-[#308ce8] underline hover:opacity-80'
            >
              {String(t('csv_template'))}
            </a>
            .
          </p>
          <p style={{ margin: 0 }}>
            <span className='font-semibold text-sd-foreground'>2. {String(t('fill_data'))}:</span>{' '}
            {String(t('use_first'))}.
          </p>
          <p style={{ margin: 0 }}>
            <span className='font-semibold text-sd-foreground'>3. {String(t('upload_file'))}:</span>{' '}
            {String(t('after_upload'))}.
          </p>
          <p style={{ margin: 0 }}>
            <span className='font-semibold text-sd-foreground'>
              4. {String(t('mandatory_fields'))}:
            </span>{' '}
            {String(t('ensure'))}.
          </p>
          <p style={{ margin: 0 }}>
            <span className='font-semibold text-sd-foreground'>5. {String(t('import_limit'))}:</span>{' '}
            {String(t('can_import'))}.
          </p>
        </div>
      </div>

      {/* How does it work Card */}
      <div className='border border-[rgb(232,230,227)] rounded-2xl p-6 bg-white'>
        <h3 className='text-base font-semibold mb-2 text-sd-foreground' style={{ marginTop: 0 }}>
          {String(t('how_to_work'))}
        </h3>
        <p className='text-sm text-[#737b8c]' style={{ margin: 0 }}>
          {String(t('importing'))}{' '}
          <a
            href='/boxy-bulk-import.csv'
            target='_blank'
            rel='noreferrer'
            className='text-[#308ce8] underline hover:opacity-80'
          >
            {String(t('csv_guide'))}
          </a>
          .
        </p>
      </div>

      {/* Upload Area */}
      <div>
        {loadingExcel ? (
          <div className='flex justify-center py-12'>
            <Spin />
          </div>
        ) : (
          <Dragger
            name={String(t('upload_file'))}
            multiple={false}
            maxCount={1}
            beforeUpload={handleUpload}
            accept='.csv'
            style={{ borderWidth: 2, borderRadius: 12, borderColor: '#e8e6e3' }}
          >
            <div className='py-4 flex flex-col items-center justify-center'>
              <div className='w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4'>
                <UploadIcon size={32} className='text-gray-400' />
              </div>
              <p className='text-base font-medium text-sd-foreground' style={{ margin: 0 }}>
                {String(t('drag_drop_csv_here'))}
              </p>
              <p className='text-sm text-[#737b8c] mb-4' style={{ marginTop: 0 }}>
                {String(t('or_click_to_browse'))}
              </p>
              <p className='text-xs font-medium text-[#dc2828]' style={{ margin: 0 }}>
                {String(t('maximum_size'))}
              </p>
            </div>
          </Dragger>
        )}
      </div>

      {/* Uploaded file display */}
      {uploadedFile && (
        <Flex className='mt-2 gap-2 flex justify-between items-center border border-[#e8e6e3] rounded-lg p-3 bg-white'>
          <Flex>
            <strong>{String(t('file_name'))}:</strong>{' '}
            <Flex className='mx-2'>{uploadedFile.name}</Flex>
          </Flex>
          <TrashIcon onClick={handleDeleteFile} color='red' />
        </Flex>
      )}
    </div>
  );
};

export default Step1;
